<template>
    <DataTable
        :header="$t('offers')"
        :error="error"
        :items="offers"
        :fields="fields"
        :total-rows="offers.max"
        :per-page.sync="tableParams.perPage"
        :current-page.sync="tableParams.page"
        :loading="loading"
        no-sort-reset
        no-local-sorting
    >

        <template #headerSide>
            <div class="d-flex gap-2">
                <b-form-input v-model="offerId" :placeholder="$t('offerId')" :disabled="loading || requestCatalogOffers.getAnyLoading()" />
                <ButtonIcon :disabled="!offerId || loading || requestCatalogOffers.getAnyLoading()" :loading="requestCatalogOffers.getLoading(`add-${offerId}`)" hide-label-while-loading @click="addOffer(offerId)">Przypisz ofertę</ButtonIcon>
            </div>
        </template>

        <template #cell(product)="data">
            <div class="d-flex flex-column gap-2">
                <b-link v-if="data.item.product" :to="{ name: 'Offers', query: { product: data.item.product } }">
                    {{ data.item.product }}
                </b-link>
                <b-link class="info text-secondary font-size-12" :to="{ name: 'OffersDetails', params: { id: data.item.id } }">
                    {{ data.item.id }}
                </b-link>
            </div>
        </template>

        <template #cell(ean)="data">
            <b-link :to="{ name: 'Offers', query: { ean: data.item.ean } }">
                {{ data.item.ean }}
            </b-link>
        </template>

        <template #cell(name)="data">
            <div class="d-flex">
                <ImageLoader
                    :src="data.item.image"
                    alt=""
                    size="50"
                    image-height="auto"
                    class="mr-2 flex-shrink-0"
                />
                <div class="d-flex flex-column gap-2">
                    <b-link class="title" :to="createItemUrl(data.item)">
                        {{ data.item.name }}
                    </b-link>
                    <div class="info text-secondary font-size-12">
                        {{ data.item.categories.join(',') }}
                    </div>
                </div>
            </div>
        </template>

        <template #cell(feed)="data">
            <div class="d-flex flex-column gap-2">
                {{ data.item.feed }}
                <div v-if="data.item.campaign" class="info">
                    {{ data.item.campaign.name }}
                </div>
            </div>
        </template>

        <template #cell(price)="data">
            <div class="nowrap">
                {{ $format.currency(data.item.price) }}
            </div>
        </template>

        <template #cell(type)="data">
            {{ getTypeLabel(data.item.type) }}<br>
            <template v-if="data.item.format">{{ data.item.format }}</template>
        </template>

        <template #cell(hidden)="data">
            <BadgeBoolean :value="data.item.hidden" true-value-badge-variant="danger" false-value-badge-variant="success" />
        </template>

        <template #cell(active)="data">
            <div class="d-flex flex-column gap-2">
                <div><BadgeBoolean :value="data.item.active" :true-value-text="$t('available2')" :false-value-text="$t('unavailable2')" /></div>
                <div class="info text-secondary font-size-12">
                    {{ $format.dateAndHour(data.item.lastAvailabilityChangedAt, 'yyyy-MM-dd') }}
                </div>
            </div>
        </template>

        <template #cell(action)="{item}">
            <div class="d-flex gap-1 justify-content-end">
                <ButtonIcon
                    size="sm"
                    icon="trash-2"
                    variant="danger"
                    :loading="requestCatalogOffers.getLoading(`delete-${item.id}`)"
                    hide-label-while-loading
                    @click="deleteOffer(item)"
                />
            </div>
        </template>

    </DataTable>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';
    import BadgeBoolean from '@/components/common/BadgeBoolean.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';

    export default {
        name: 'Offers',
        components: {
            DataTable,
            BadgeBoolean,
            ImageLoader,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestOffers: this.$req('offers/offer'),
                requestVariantOffers: this.$req('catalog/variantOffers'),
                requestTypesAndFormats: this.$req('offers/typesAndFormats'),
                requestCatalogOffers: this.$req('catalog/offers'),
                tableParams: {
                    perPage: 50,
                    page: 1,
                },
                offerId: null,
            };
        },
        computed: {
            variant() {
                return this.parentComponent.variant;
            },
            offers() {
                return this.requestVariantOffers.getData()?.items || [];
            },
            loading() {
                return (!this.variant && this.parentComponent.loading) || this.requestVariantOffers.getLoading() || this.requestTypesAndFormats.getLoading();
            },
            error() {
                return this.requestVariantOffers.getError();
            },
            typesAndFormats() {
                return this.requestTypesAndFormats.getData() || [];
            },
            fields() {
                return [
                    { key: 'product', label: this.$t('productOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                    { key: 'action', label: '' },
                ];
            },
        },
        watch: {
            variant: {
                handler(variant) {
                    if (variant) this.fetchOffers();
                },
                immediate: true,
            },
            tableParams: {
                handler: 'fetchOffers',
                deep: true,
            },
        },
        methods: {
            fetchOffers() {
                this.requestVariantOffers.get({ requestParams: this.tableParams, urlParams: { id: this.variant.gtin } }).catch(() => {});
            },
            fetchTypesAndFormats() {
                return this.requestTypesAndFormats.get().catch(() => {});
            },
            getTypeLabel(type) {
                return this.typesAndFormats.find(item => item.value === type)?.label || type;
            },
            createItemUrl(item) {
                return {
                    name: 'OffersDetails',
                    params: { id: item.id, parentUrl: this.$route.fullPath },
                    query: {},
                };
            },
            deleteOffer(item) {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmYouWantDelete'), {
                        title: this.$t('confirm'),
                        okTitle: this.$t('yes'),
                        cancelTitle: this.$t('cancel'),
                        okVariant: 'danger',
                    })
                    .then(value => {
                        if (value) {
                            this.requestCatalogOffers
                                .id(`delete-${item.id}`)
                                .delete({ urlParams: { id: item.id } })
                                .then(this.fetchOffers)
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            addOffer(id) {
                this.requestCatalogOffers
                    .id(`add-${id}`)
                    .post({ requestParams: { id, variant: this.variant.gtin } })
                    .then(() => {
                        this.offerId = null;
                        this.fetchOffers();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
        },
        created() {
            this.fetchTypesAndFormats();
        },
    };
</script>
